import {
  UnitAndCalories,
  DrinksWithTotal,
  DrinksTotals
} from "@dcc/types/drinkingDay";
import { sumBy, max, pipe } from "@dcc/common/functionalUtils";
import {
  monthlyUnitAndCaloriesConverter,
  monthlyTotalDrinksConverter
} from "./monthlyConverters";
import { DrinkFrequency } from "@dcc/types/drinkFrequency";
import { DrinkerTypes } from "@dcc/store/score/getters/drinkerTypesGetters";
import { weeklyUnitAndCaloriesConverter } from "./weeklyConverters";
import { roundToNDecimalPlaces } from "@dcc/common/rounding";
import { drinkUnitAndCalories } from "@dcc/constants/drinkConstants";

const sumWeeklyUnitsAndCalories = (
  unitsAndCaloriesPerDay: UnitAndCalories[]
): UnitAndCalories => ({
  units: sumBy(unitsAndCaloriesPerDay, "units"),
  calories: sumBy(unitsAndCaloriesPerDay, "calories")
});

export const getWeeklyUnitsAndCalories = ({
  unitsAndCaloriesPerDay,
  drinkFrequency,
  monthlyDrinkingDays
}: {
  unitsAndCaloriesPerDay: UnitAndCalories[];
  drinkFrequency: DrinkFrequency;
  monthlyDrinkingDays: number;
}): UnitAndCalories => {
  const sumCaloriesAndUnits = sumWeeklyUnitsAndCalories(unitsAndCaloriesPerDay);

  const converter = pipe(
    weeklyUnitAndCaloriesConverter()[drinkFrequency],
    monthlyUnitAndCaloriesConverter(monthlyDrinkingDays)[drinkFrequency]
  );

  return {
    calories: converter(sumCaloriesAndUnits.calories),
    units: converter(sumCaloriesAndUnits.units)
  };
};

export const getTotalWeeklyDrinks = ({
  drinksByDay,
  drinkFrequency,
  numberOfMonthlyDrinkingDays
}: {
  drinksByDay: DrinksWithTotal[];
  drinkFrequency: DrinkFrequency;
  numberOfMonthlyDrinkingDays: number;
}) => {
  const totalDrinks = sumBy(drinksByDay, "amount");
  const converter = monthlyTotalDrinksConverter(numberOfMonthlyDrinkingDays)[
    drinkFrequency
  ];
  return Math.floor(converter(totalDrinks));
};

export const getHighestUnitsPerDay = (
  unitsAndCaloriesPerDay: UnitAndCalories[]
) => max(unitsAndCaloriesPerDay, "units");

export const getUnitsAsDrinks = (units: number) => {
  return {
    beer: roundToNDecimalPlaces(units / drinkUnitAndCalories.beer.units, 1),
    wine: roundToNDecimalPlaces(units / drinkUnitAndCalories.wine.units, 1),
    pint: roundToNDecimalPlaces(units / drinkUnitAndCalories.pint.units, 1),
    spirit: roundToNDecimalPlaces(units / drinkUnitAndCalories.spirit.units, 1)
  };
};

export const getDrinkValues = ({
  drinksByDay
}: {
  drinksByDay: DrinksTotals[];
}) => {
  const beer = sumBy(drinksByDay, "beer");
  const wine = sumBy(drinksByDay, "wine");
  const spirit = sumBy(drinksByDay, "spirit");
  const pint = sumBy(drinksByDay, "pint");
  return {
    beer: beer,
    wine: wine,
    pint: pint,
    spirit: spirit
  };
};

export const getDrinkerType = ({
  drinkFrequency,
  drinksByDay
}: {
  drinkFrequency: DrinkFrequency;
  drinksByDay: DrinksTotals[];
}) => {
  if (drinkFrequency == DrinkFrequency.Never) {
    return {
      drinkerType: DrinkerTypes.NEVER
    };
  }
  if (drinkFrequency == DrinkFrequency.Monthly) {
    return {
      drinkerType: DrinkerTypes.MONTHLY
    };
  } else {
    if (drinksByDay.length >= 4) {
      return {
        drinkerType: DrinkerTypes.FOURDAYSORMORE
      };
    } else {
      if (drinksByDay.length <= 3) {
        return {
          drinkerType: DrinkerTypes.THREEDAYSORLESS
        };
      }
    }
    return {
      drinkerType: "undefined"
    };
  }
};
